/**
 * The fine-tune action log: append-only, replayed over every compile.
 *
 * `replayLog(compiled, log)` is the primary API. It folds the log —
 *
 *  1. **Undo** (descending seq): an effective `undo` reverts its target; an
 *     undone `undo` has no effect, so undoing an undo is a redo.
 *  2. **Rebase** (ascending): the latest effective `rebase` of an entry
 *     replaces its anchor / fingerprint (keep-as-text, re-anchor).
 *  3. **Dependencies** (ascending): an entry anchored to the output of an
 *     undone entry (`{ fromSeq }`) is undone with it.
 *
 * — then applies the surviving content entries, in seq order, as patches
 * (`id = String(seq)`) and reports every entry as `applied`, `conflict` or
 * `undone`. The log itself is never rewritten by a replay.
 *
 * Own-field entries (`defineField` / `updateField` / `removeField`) are not
 * patches: they fold into the own fields in effect (`fields.ts`), which the
 * replay hands to the reference resolver so `custom_…` references print
 * their current values. Pass `references.values` for that.
 */
import { formatLogAddress, resolveAnchor, resolvePartAnchor, toPatchAnchor } from './address.js';
import { applyPatches } from './apply.js';
import { foldCustomFields } from './fields.js';
import { applyRebase, effectiveAnchoring, foldLog, isContentEntry, isFieldEntry, isProposal, isSuggestionDecision } from './fold.js';
import { groupRevertTargets, undoRefusal } from './groups.js';
import { ACTION_LOG_SCHEMA, ACTION_LOG_VERSION } from './types.js';
export { isContentEntry, isFieldEntry, isProposal, isSuggestionDecision };
export const emptyActionLog = () => ({ schema: ACTION_LOG_SCHEMA, version: ACTION_LOG_VERSION, entries: [] });
export const nextSeq = (log) => log.entries.reduce((max, e) => Math.max(max, e.seq), 0) + 1;
/** Appends an entry with the next seq. Returns the new log and the entry. */
export function appendAction(log, action) {
    const entry = { ...action, seq: nextSeq(log) };
    return { log: { ...log, entries: [...log.entries, entry] }, entry };
}
/** An undo the group rules refuse (see `groups.ts`). */
export class ActionLogError extends Error {
    reason;
    targetSeq;
    constructor(reason, targetSeq) {
        super(`undo of #${targetSeq} refused: ${reason}`);
        this.reason = reason;
        this.targetSeq = targetSeq;
    }
}
/**
 * Reverts an earlier entry (undo; redo = undo of an undo). Throws
 * `ActionLogError` when the group rules refuse it — a superseded version,
 * an already undone entry, or a stale redo.
 */
export function undoAction(log, targetSeq, meta, reason = 'undo', options = {}) {
    const refusal = undoRefusal(log, targetSeq, options.report);
    if (refusal)
        throw new ActionLogError(refusal, targetSeq);
    return appendAction(log, { op: 'undo', ...meta, payload: { targetSeq, reason } });
}
/** "Återställ": undo of a `removeNode` entry. */
export const restoreRemoval = (log, removeSeq, meta, options = {}) => undoAction(log, removeSeq, meta, 'restore', options);
/** Conflict resolution "drop": discard the conflicting edit. */
export const dropAction = (log, targetSeq, meta, options = {}) => undoAction(log, targetSeq, meta, 'drop', options);
/** "Ångra senaste" for a group: undo its head, so the previous version is in effect again. */
export function undoGroupHead(log, group, meta, options = {}) {
    if (group.headSeq == null)
        throw new ActionLogError('already_undone', group.rootSeq);
    return undoAction(log, group.headSeq, meta, 'undo', options);
}
/**
 * "Återställ original" for a group: one batch of undos (see
 * `groupRevertTargets`) after which nothing of the group is in effect.
 */
export function revertGroup(log, group, meta, options = {}) {
    let current = log;
    const entries = [];
    for (const seq of groupRevertTargets(group)) {
        const target = current.entries.find((e) => e.seq === seq);
        const out = undoAction(current, seq, meta, target?.op === 'removeNode' || target?.op === 'removeRow' ? 'restore' : 'undo', options);
        current = out.log;
        entries.push(out.entry);
    }
    return { log: current, entries };
}
/** The group a seq belongs to, if any. */
export const groupOfSeq = (groups, seq) => groups.find((g) => g.seqs.includes(seq) || g.actionSeqs.includes(seq));
/**
 * The log reduced to what is in effect after `report`'s replay: content
 * entries that are applied or in conflict, with their effective rebase
 * folded into anchor / fingerprint, minus superseded `replaceText`s
 * nothing anchors to. Seqs are kept (inserted clause refs derive from
 * them). Replaying it over the same base yields the same document as the
 * full log. Not a replacement for the log — an issue keeps both.
 */
export function effectiveActionLog(log, report) {
    const folded = foldLog(log);
    const rows = new Map(report.map((r) => [r.seq, r]));
    const sorted = [...log.entries].sort((a, b) => a.seq - b.seq);
    const live = sorted
        .filter((e) => {
        if (!isContentEntry(e) || folded.malformed.has(e.seq) || folded.undone.has(e.seq))
            return false;
        const suggestion = folded.suggestions.get(e.seq);
        if (suggestion && suggestion.state !== 'accepted')
            return false;
        const status = rows.get(e.seq)?.status;
        return status === 'applied' || status === 'conflict';
    })
        .map((e) => {
        const { anchor, fingerprint, to, toFingerprint } = applyRebase(e, folded.rebases.get(e.seq));
        const { fingerprint: _f, proposal: _p, ...rest } = e;
        const out = { ...rest, anchor, ...(fingerprint ? { fingerprint } : {}) };
        if (out.op === 'moveSection') {
            out.payload = { position: out.payload.position, to: to, ...(toFingerprint ? { toFingerprint } : {}) };
        }
        return out;
    });
    const needed = new Set();
    for (const e of live) {
        const deps = [e.anchor, e.op === 'moveSection' ? e.payload.to : undefined];
        for (const a of deps)
            if (a && 'fromSeq' in a)
                needed.add(a.fromSeq);
    }
    const content = live.filter((e) => rows.get(e.seq)?.supersededBy == null || needed.has(e.seq));
    // Own fields in effect, compacted to one definition each (at its define seq).
    const fields = foldCustomFields(log, folded).fields;
    const definitions = [...fields.values()].map((f) => {
        const define = sorted.find((e) => e.seq === f.definedBy);
        return {
            op: 'defineField',
            seq: f.definedBy,
            author: define.author,
            at: define.at,
            payload: { key: f.key, label: f.label, type: f.type, ...(f.help ? { help: f.help } : {}) },
        };
    });
    const entries = [...definitions, ...content].sort((a, b) => a.seq - b.seq);
    return { schema: log.schema, version: log.version, entries };
}
/** Conflict resolution "re-anchor": the edit now applies to another node. */
export const reanchorAction = (log, targetSeq, anchor, fingerprint, meta) => appendAction(log, {
    op: 'rebase',
    ...meta,
    anchor,
    ...(fingerprint ? { fingerprint } : {}),
    payload: { targetSeq, resolution: 'reanchor' },
});
/**
 * Conflict resolution "keep as text": the human has seen what the target
 * compiles to now and keeps their edit over it. Re-fingerprints the entry's
 * (effective) anchor against the current compiled document. `null` when the
 * anchor no longer resolves (that needs re-anchor or drop) or the entry is
 * not a content entry.
 */
export function keepAsText(log, targetSeq, compiled, meta) {
    const target = log.entries.find((e) => e.seq === targetSeq);
    if (!target || !isContentEntry(target))
        return null;
    const { anchor } = effectiveAnchoring(log, target);
    if ('fromSeq' in anchor)
        return null;
    const resolved = resolveAnchor(compiled, anchor) ?? resolvePartAnchor(compiled, anchor);
    if (!resolved)
        return null;
    return appendAction(log, {
        op: 'rebase',
        ...meta,
        fingerprint: resolved.fingerprint,
        payload: { targetSeq, resolution: 'keep_as_text' },
    });
}
/**
 * The content entries a replay applies, as engine patches (`id = String(seq)`).
 * Open suggestions are left out unless `includeOpenSuggestions` (the
 * proposal overlay); rejected ones always are.
 */
export function logToPatches(log, options = {}) {
    const folded = foldLog(log);
    const out = [];
    for (const e of [...log.entries].sort((a, b) => a.seq - b.seq)) {
        if (folded.malformed.has(e.seq) || folded.undone.has(e.seq) || !isContentEntry(e))
            continue;
        const suggestion = folded.suggestions.get(e.seq);
        if (suggestion?.state === 'rejected' || (suggestion?.state === 'open' && !options.includeOpenSuggestions))
            continue;
        const { anchor, fingerprint, to, toFingerprint } = applyRebase(e, folded.rebases.get(e.seq));
        const base = {
            id: String(e.seq),
            author: e.author,
            at: e.at,
            anchor: toPatchAnchor(anchor),
            ...(e.blockVersion ? { blockVersion: e.blockVersion } : {}),
            ...(e.structureFingerprint ? { structureFingerprint: e.structureFingerprint } : {}),
        };
        switch (e.op) {
            case 'replaceText':
                out.push({ ...base, op: e.op, fingerprint: fingerprint, payload: e.payload });
                break;
            case 'removeNode':
            case 'removeRow':
                out.push({ ...base, op: e.op, fingerprint: fingerprint });
                break;
            case 'replaceRow':
                out.push({ ...base, op: e.op, fingerprint: fingerprint, payload: e.payload });
                break;
            case 'insertParagraph':
            case 'insertClause':
            case 'insertRow':
                out.push({ ...base, op: e.op, ...(fingerprint ? { fingerprint } : {}), payload: e.payload });
                break;
            case 'moveSection':
                out.push({
                    ...base,
                    op: e.op,
                    fingerprint: fingerprint,
                    payload: { position: e.payload.position, to: toPatchAnchor(to), ...(toFingerprint ? { toFingerprint } : {}) },
                });
                break;
        }
    }
    return out;
}
/**
 * Replays the action log over a compiled (unresolved or resolved) base.
 * The result's `document` is unresolved — assemble it (or use
 * `buildPatchedDocument` / `assemblePatched`, which do).
 *
 * With open suggestions in the log, a second pass applies them on top of
 * the clean replay to classify each one: `proposed` when it applies,
 * `stale` when it conflicts, or when a later adaptation (not a suggestion)
 * of the same node supersedes it.
 */
export function replayLog(compiled, log, options = {}) {
    const { proposals: proposalMode, ...applyOptions } = options;
    const folded = foldLog(log);
    const fields = foldCustomFields(log, folded);
    const opts = { ...applyOptions, references: { ...applyOptions.references, customFields: fields.fields } };
    const result = applyPatches(compiled, logToPatches(log), opts);
    const applied = new Map(result.applied.map((a) => [a.patchId, a]));
    const conflicts = new Map(result.conflicts.map((c) => [c.patchId, c]));
    const openSeqs = [...folded.suggestions]
        .filter(([seq, s]) => s.state === 'open' && !folded.undone.has(seq) && !folded.malformed.has(seq))
        .map(([seq]) => seq);
    const overlayMode = proposalMode === 'overlay' && openSeqs.length > 0;
    const overlay = openSeqs.length > 0
        ? applyPatches(compiled, logToPatches(log, { includeOpenSuggestions: true }), { ...opts, track: overlayMode && opts.track === true })
        : null;
    const overlayApplied = new Map((overlay?.applied ?? []).map((a) => [a.patchId, a]));
    const overlayConflicts = new Map((overlay?.conflicts ?? []).map((c) => [c.patchId, c]));
    const report = [...log.entries]
        .sort((a, b) => a.seq - b.seq)
        .map((e) => {
        const row = { seq: e.seq, op: e.op, author: e.author, at: e.at };
        if (folded.malformed.has(e.seq))
            return { ...row, status: 'conflict', reason: 'invalid_patch' };
        const suggestion = folded.suggestions.get(e.seq);
        const sugg = suggestion
            ? { layer: 'suggestion', ...(suggestion.decidedBy != null ? { decidedBy: suggestion.decidedBy } : {}) }
            : {};
        const undoneBy = folded.undone.get(e.seq);
        const fieldKey = isFieldEntry(e) ? fields.keyOf.get(e.seq) : undefined;
        if (undoneBy != null) {
            return {
                ...row,
                status: 'undone',
                undoneBy,
                ...(isContentEntry(e) ? { address: formatLogAddress(e.anchor) } : {}),
                ...(fieldKey != null ? { fieldKey } : {}),
                ...sugg,
                ...(suggestion?.state === 'accepted' ? { suggestion: 'accepted' } : {}),
            };
        }
        if (isFieldEntry(e)) {
            return fields.applied.has(e.seq)
                ? { ...row, status: 'applied', ...(fieldKey != null ? { fieldKey } : {}) }
                : { ...row, status: 'conflict', reason: 'invalid_patch', ...(fieldKey != null ? { fieldKey } : {}) };
        }
        if (!isContentEntry(e)) {
            return folded.invalid.has(e.seq) ? { ...row, status: 'conflict', reason: 'invalid_target' } : { ...row, status: 'applied' };
        }
        const id = String(e.seq);
        if (suggestion?.state === 'rejected') {
            return { ...row, ...sugg, status: 'rejected', suggestion: 'rejected', address: formatLogAddress(e.anchor) };
        }
        if (suggestion?.state === 'open') {
            const c = overlayConflicts.get(id);
            if (c) {
                return { ...row, ...sugg, status: 'stale', suggestion: 'stale', reason: c.reason, address: c.address ?? formatLogAddress(e.anchor) };
            }
            const a = overlayApplied.get(id);
            const by = a?.supersededBy != null ? Number(a.supersededBy) : null;
            const stale = !a || (by != null && folded.suggestions.get(by)?.state !== 'open');
            return {
                ...row,
                ...sugg,
                status: stale ? 'stale' : 'proposed',
                suggestion: stale ? 'stale' : 'proposed',
                address: a?.address ?? formatLogAddress(e.anchor),
                ...(a?.instanceId ? { instanceId: a.instanceId } : {}),
                ...(by != null ? { supersededBy: by } : {}),
                ...(!a ? { reason: 'invalid_patch' } : {}),
            };
        }
        const accepted = suggestion?.state === 'accepted' ? { suggestion: 'accepted' } : {};
        const rebasedBy = folded.rebases.get(e.seq)?.seq;
        const extra = { ...(rebasedBy != null ? { rebasedBy } : {}), ...sugg, ...accepted };
        const c = conflicts.get(id);
        if (c)
            return { ...row, ...extra, status: 'conflict', reason: c.reason, ...(c.address ? { address: c.address } : {}) };
        const a = applied.get(id);
        if (a) {
            return {
                ...row,
                ...extra,
                status: 'applied',
                address: a.address,
                ...(a.instanceId ? { instanceId: a.instanceId } : {}),
                ...(a.supersededBy != null ? { supersededBy: Number(a.supersededBy) } : {}),
                ...(a.carriedOver ? { carriedOver: a.carriedOver } : {}),
            };
        }
        return { ...row, ...extra, status: 'conflict', reason: 'invalid_patch' };
    });
    const document = overlayMode && overlay ? overlay.document : result.document;
    if (opts.track)
        stampChangeLayers(document, folded.suggestions);
    return { ...result, document, report };
}
/**
 * Stamps `layer` (and `suggestion`) on every edit mark of a tracked
 * document that names a change (`patchId` with `change`, or `movedBy`).
 */
export function stampChangeLayers(document, suggestions) {
    const stamp = (mark) => {
        const id = mark.change && mark.patchId ? mark.patchId : mark.movedBy;
        if (!id)
            return;
        const s = suggestions.get(Number(id));
        mark.layer = s ? 'suggestion' : 'adaptation';
        if (s)
            mark.suggestion = s.state === 'accepted' ? 'accepted' : 'proposed';
        else
            delete mark.suggestion;
    };
    const seen = new Set();
    const visit = (value) => {
        if (value == null || typeof value !== 'object' || seen.has(value))
            return;
        seen.add(value);
        if (Array.isArray(value)) {
            for (const v of value)
                visit(v);
            return;
        }
        const o = value;
        for (const key of ['edit', 'captionEdit']) {
            const mark = o[key];
            if (mark && typeof mark === 'object')
                stamp(mark);
        }
        for (const [key, v] of Object.entries(o))
            if (key !== 'edit' && key !== 'captionEdit' && v && typeof v === 'object')
                visit(v);
    };
    for (const part of document.parts)
        visit(part);
}
/** Entries the human must resolve (keep as text / drop / re-anchor) before issue. */
export const replayConflicts = (report) => report.filter((r) => r.status === 'conflict');
//# sourceMappingURL=log.js.map